import { useCallback, useEffect, useRef, useState, type ChangeEvent, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import { compileProjectPreviewDocument } from "@hyperframes/react/browser";
import { ProductCardView, productCardCss } from "@hyperframes/react/product-card";
import { DEFAULT_CANVAS_BACKGROUND, DEFAULT_CARD_DURATION_MS, MAX_CARD_DURATION_MS, MIN_CARD_DURATION_MS, type CarouselVariant, type CreateShopVideoResponse, type ExportFormat, type ProductCardEdit } from "../src/contracts.js";
import { createShopVideoDraft } from "../src/create-shop-video.js";
import { DEFAULT_EXPORT_FORMAT, exportFilename, getExportFormat } from "../src/export-formats.js";
import { MockEtsyDataSource } from "../src/mock-etsy-data-source.js";
import { createPreviewProject } from "../src/preview-project.js";
import { ClientRenderError, renderPreviewToMp4, type Mp4RenderProgress } from "./render-mp4.js";
import { resolvePageVariant, type PageVariant } from "./page-variants.js";
import { HeaderNoLinks } from "./components/HeaderNoLinks.js";
import { ProcessOverview } from "./components/ProcessOverview.js";
import { VideoFormatSelectionBox } from "./components/VideoFormatSelectionBox.js";
import { VideoFormatSelectionMobile } from "./components/VideoFormatSelectionMobile.js";

const VARIANTS: Array<{ id: CarouselVariant; label: string; description: string; previewImage: string }> = [
  { id: "curved-focus-carousel", label: "Circular", description: "Dynamic & modern", previewImage: "/assets/minimalist-rotation-carousel.webp" },
  { id: "focus-carousel", label: "Horizontal", description: "Clean & versatile", previewImage: "/assets/minimalist-horizontal-carousel.webp" },
  { id: "vertical-focus-carousel", label: "Vertical", description: "Perfect for Reels", previewImage: "/assets/minimalist-vertical-carouseI.webp" },
];

const SOLID_BACKGROUNDS = ["#181820", "#0f172a", "#332238", "#56303a", "#7c2d12", "#854d0e", "#14532d", "#164e63", "#312e81", "#f1e7dc"];
const GRADIENT_BACKGROUNDS = ["linear-gradient(135deg,#181820,#303044)", "linear-gradient(135deg,#172033,#334155)", "linear-gradient(135deg,#2a103d,#ff4d8d)", "linear-gradient(135deg,#ff9a8b,#ff6a88 45%,#8a4fff)", "linear-gradient(135deg,#0f766e,#38bdf8)", "linear-gradient(135deg,#f7d9a6,#f48fb1 48%,#a78bfa)"];
const HEADLINE_COLORS = ["#fffaf6", ...SOLID_BACKGROUNDS];

const PREVIEW_PROGRESS_INTERVAL_MS = 250;
const PLAYER_CONTROLS_HIDE_DELAY_MS = 2_800;
const dataSource = new MockEtsyDataSource();

type WebkitFullscreenDocument = Document & {
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element | null;
  webkitFullscreenEnabled?: boolean;
};

type WebkitFullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

type VideoHeadline = {
  headline: string;
  eyebrow: string;
  showHeading: boolean;
  color: string;
  eyebrowColor: string;
};

function formatTime(value: number): string {
  const seconds = Math.max(0, value);
  return `00:${String(Math.floor(seconds)).padStart(2, "0")}:${String(Math.floor((seconds % 1) * 30)).padStart(2, "0")}`;
}

function previewImageSource(image: string): string {
  const match = image.match(/^\/assets\/catalog\/([^/?#]+)\.(?:png|jpe?g|webp)$/i);
  return match ? `/assets/catalog-preview/${match[1]}.webp` : image;
}

function MetaProcessSteps(): ReactElement {
  const steps = [
    { label: "Add your creative", description: "Start with your product images.", icon: "image" },
    { label: "Choose an Ad type", description: "Choose a format and click render.", icon: "render" },
    { label: "Download your video for free", description: "Use your video on Meta and beyond.", icon: "send" },
  ];
  return <ol className="meta-process" aria-label="How Shop Motion works">
    {steps.map((step, index) => <li key={step.label}>
      <span className="meta-process-number" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
      <span className={`meta-process-icon is-${step.icon}`} aria-hidden="true"><i/></span>
      <span className="meta-process-copy"><span><b>{index + 1}</b><strong>{step.label}</strong></span><small>{step.description}</small></span>
    </li>)}
  </ol>;
}

function RenderProgressModal({ progress, onCancel }: { progress: Mp4RenderProgress; onCancel: () => void }): ReactElement {
  const progressPercent = Math.round(progress.progress * 100);
  return createPortal(<div className="render-modal-backdrop">
    <section className="render-modal" role="dialog" aria-modal="true" aria-labelledby="render-modal-title" aria-describedby="render-modal-description">
      <span className="render-modal-kicker">Creating your MP4</span>
      <strong id="render-modal-title">Rendering your video…</strong>
      <span className="render-modal-status">{progressPercent}% · frame {progress.frame} of {progress.frameCount}</span>
      <progress aria-label={`MP4 render ${progressPercent}% complete`} max="1" value={progress.progress}/>
      <small id="render-modal-description">Keep this window open. Your video is rendered securely in your browser.</small>
      <button type="button" autoFocus onClick={onCancel}>Cancel render</button>
    </section>
  </div>, document.body);
}

function VideoPreview({ html, exportHtml, duration, width, height, fps, filename, title }: { html: string; exportHtml: string; duration: number; width: number; height: number; fps: number; filename: string; title: string }): ReactElement {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const previewShellRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const controlsHideTimerRef = useRef<number | null>(null);
  const exportControllerRef = useRef<AbortController | null>(null);
  const lastProgressUpdateRef = useRef(-Infinity);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [fallbackFullscreen, setFallbackFullscreen] = useState(false);
  const [nativeFullscreenSupported] = useState(() => {
    const fullscreenDocument = document as WebkitFullscreenDocument;
    const fullscreenElement = document.documentElement as WebkitFullscreenElement;
    return document.fullscreenEnabled || fullscreenDocument.webkitFullscreenEnabled === true || typeof fullscreenElement.webkitRequestFullscreen === "function";
  });
  const [time, setTime] = useState(0);
  const [renderProgress, setRenderProgress] = useState<Mp4RenderProgress | null>(null);
  const [renderError, setRenderError] = useState("");
  const [download, setDownload] = useState<{ url: string; size: number } | null>(null);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 900px)").matches);
  const rendering = renderProgress !== null;
  const isFullscreen = fullscreen || fallbackFullscreen;

  const clearControlsHideTimer = useCallback(() => {
    if (controlsHideTimerRef.current === null) return;
    window.clearTimeout(controlsHideTimerRef.current);
    controlsHideTimerRef.current = null;
  }, []);

  const hideControls = useCallback(() => {
    clearControlsHideTimer();
    if (!controlsRef.current?.contains(document.activeElement)) setControlsVisible(false);
  }, [clearControlsHideTimer]);

  const scheduleControlsHide = useCallback(() => {
    clearControlsHideTimer();
    controlsHideTimerRef.current = window.setTimeout(() => {
      controlsHideTimerRef.current = null;
      if (!controlsRef.current?.contains(document.activeElement)) setControlsVisible(false);
    }, PLAYER_CONTROLS_HIDE_DELAY_MS);
  }, [clearControlsHideTimer]);

  const revealControls = useCallback(() => {
    setControlsVisible(true);
    scheduleControlsHide();
  }, [scheduleControlsHide]);

  const syncPreviewQuality = useCallback(() => {
    const quality = window.matchMedia("(max-width: 720px)").matches ? "mobile" : "full";
    setReady(false);
    setPlaying(false);
    iframeRef.current?.contentWindow?.postMessage({ source: "hf-editor-preview", type: "pause" }, "*");
    iframeRef.current?.contentWindow?.postMessage({ source: "hf-editor-preview", type: "quality", quality }, "*");
  }, []);

  const seek = useCallback((next: number) => {
    const bounded = Math.max(0, Math.min(next, Math.max(0, duration - 1 / fps)));
    setTime(bounded);
    iframeRef.current?.contentWindow?.postMessage({ source: "hf-editor-preview", type: "seek", time: bounded }, "*");
  }, [duration, fps]);

  const stop = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage({ source: "hf-editor-preview", type: "pause" }, "*");
    setPlaying(false);
  }, []);

  useEffect(() => {
    exportControllerRef.current?.abort();
    setReady(false);
    setPlaying(false);
    setTime(0);
    setRenderProgress(null);
    setRenderError("");
    setDownload((current) => {
      if (current) URL.revokeObjectURL(current.url);
      return null;
    });
    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow || event.data?.source !== "hf-deterministic-preview") return;
      if (event.data.type === "ready") {
        lastProgressUpdateRef.current = -Infinity;
        syncPreviewQuality();
      } else if (event.data.type === "quality") {
        setReady(true);
      } else if (event.data.type === "frame") setTime(Number(event.data.time) || 0);
      else if (event.data.type === "progress") {
        const nextPlaying = event.data.playing === true;
        const now = performance.now();
        if (!nextPlaying || now - lastProgressUpdateRef.current >= PREVIEW_PROGRESS_INTERVAL_MS) {
          lastProgressUpdateRef.current = now;
          setTime(Number(event.data.time) || 0);
        }
        setPlaying(nextPlaying);
      }
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      iframeRef.current?.contentWindow?.postMessage({ source: "hf-editor-preview", type: "pause" }, "*");
      exportControllerRef.current?.abort();
    };
  }, [html, seek, syncPreviewQuality]);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 900px)");
    const update = () => {
      setIsMobile(query.matches);
      syncPreviewQuality();
    };
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, [syncPreviewQuality]);

  useEffect(() => () => {
    if (download) URL.revokeObjectURL(download.url);
  }, [download]);

  useEffect(() => clearControlsHideTimer, [clearControlsHideTimer]);

  useEffect(() => {
    const fullscreenDocument = document as WebkitFullscreenDocument;
    const onFullscreenChange = () => {
      const activeElement = document.fullscreenElement ?? fullscreenDocument.webkitFullscreenElement ?? null;
      const active = activeElement === previewShellRef.current;
      setFullscreen(active);
      if (active) {
        setFallbackFullscreen(false);
        revealControls();
      }
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
    };
  }, [revealControls]);

  useEffect(() => {
    if (!fallbackFullscreen) return;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFallbackFullscreen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [fallbackFullscreen]);

  useEffect(() => {
    if (!rendering) return;
    const appRoot = document.getElementById("app-root");
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") exportControllerRef.current?.abort();
    };
    appRoot?.setAttribute("inert", "");
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      appRoot?.removeAttribute("inert");
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [rendering]);

  function togglePlayback(): void {
    revealControls();
    if (playing) {
      stop();
      return;
    }
    const initialTime = time >= duration - 1 / fps ? 0 : time;
    if (initialTime !== time) setTime(0);
    setPlaying(true);
    iframeRef.current?.contentWindow?.postMessage({ source: "hf-editor-preview", type: "play", time: initialTime }, "*");
  }

  async function toggleFullscreen(): Promise<void> {
    revealControls();
    if (fallbackFullscreen) {
      setFallbackFullscreen(false);
      return;
    }
    const fullscreenDocument = document as WebkitFullscreenDocument;
    const activeElement = document.fullscreenElement ?? fullscreenDocument.webkitFullscreenElement ?? null;
    if (activeElement) {
      if (document.exitFullscreen) await document.exitFullscreen();
      else await fullscreenDocument.webkitExitFullscreen?.();
      return;
    }
    const target = previewShellRef.current as WebkitFullscreenElement | null;
    if (!target) return;
    if (!nativeFullscreenSupported) {
      setFallbackFullscreen(true);
      return;
    }
    try {
      if (target.requestFullscreen) await target.requestFullscreen();
      else await target.webkitRequestFullscreen?.();
    } catch {
      setFallbackFullscreen(true);
    }
  }

  async function renderMp4(): Promise<void> {
    const iframe = iframeRef.current;
    if (!iframe || !ready || isMobile) return;
    stop();
    if (download) URL.revokeObjectURL(download.url);
    setDownload(null);
    setRenderError("");
    const controller = new AbortController();
    exportControllerRef.current = controller;
    try {
      const blob = await renderPreviewToMp4({ html: exportHtml, duration, width, height, fps, signal: controller.signal, onProgress: setRenderProgress });
      if (controller.signal.aborted) return;
      setDownload({ url: URL.createObjectURL(blob), size: blob.size });
      setRenderProgress(null);
      seek(0);
    } catch (error) {
      setRenderProgress(null);
      if (error instanceof ClientRenderError && error.code === "aborted") return;
      setRenderError(error instanceof Error ? error.message : "The browser could not render the MP4.");
    } finally {
      if (exportControllerRef.current === controller) exportControllerRef.current = null;
    }
  }

  const previewStyle = { "--preview-ratio": width / height } as CSSProperties;

  return <>
    <div className={`video-player${rendering ? " is-rendering" : ""}`}>
      <div
        ref={previewShellRef}
        className={`preview-shell${controlsVisible ? " controls-visible" : ""}${fallbackFullscreen ? " is-fallback-fullscreen" : ""}`}
        onPointerEnter={(event) => { if (event.pointerType === "mouse") revealControls(); }}
        onPointerMove={(event) => { if (event.pointerType === "mouse") revealControls(); }}
        onPointerLeave={(event) => { if (event.pointerType === "mouse") hideControls(); }}
        onFocusCapture={revealControls}
        onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) scheduleControlsHide(); }}
      >
        <div className="preview-canvas" style={previewStyle}><iframe key={html} ref={iframeRef} srcDoc={html} title={title} sandbox="allow-scripts allow-same-origin" /></div>
        <button className="player-surface" type="button" aria-label="Show video controls" aria-expanded={controlsVisible} aria-controls="shop-motion-player-controls" onPointerDown={revealControls}/>
        <div ref={controlsRef} id="shop-motion-player-controls" className="player-controls" aria-hidden={!controlsVisible} onPointerDown={revealControls}>
          <button type="button" onClick={togglePlayback} disabled={!ready || rendering} aria-label={playing ? "Pause video" : "Play video"}>{playing ? "Ⅱ" : "▶"}</button>
          <input aria-label="Video playhead" type="range" min="0" max={duration} step={1 / 30} value={time} disabled={rendering} onChange={(event) => { stop(); seek(Number(event.target.value)); }}/>
          <span>{formatTime(time)} / {formatTime(duration)}</span>
          <button className="fullscreen-button" type="button" disabled={rendering} aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"} onClick={() => void toggleFullscreen()}>{isFullscreen ? "↙" : "⛶"}</button>
        </div>
      </div>
      <div className={`export-controls${isMobile ? " is-mobile-desktop-cta" : ""}`}>
        {isMobile ? <div className="desktop-render-button">
          <span className="desktop-render-icon" aria-hidden="true">▣</span>
          <span className="desktop-render-copy"><strong>Create on desktop</strong><small>Open MotionTwist on your computer to create and download your MP4.</small></span>
          <img src="/assets/motiontwist-logo.png" alt="" aria-hidden="true"/>
          <em>Free to use. No watermarks.</em>
        </div> : rendering ? <button type="button" className="rendering-button" disabled>Rendering MP4…</button> : <button type="button" className="render-button" disabled={!ready} onClick={() => void renderMp4()}><span aria-hidden="true">✦</span> Render MP4 <b aria-hidden="true">→</b></button>}
        {download && !isMobile ? <a href={download.url} download={filename}>Download MP4 <span>{(download.size / 1024 / 1024).toFixed(1)} MB</span></a> : null}
        {!isMobile ? <small>Free to use. No watermarks.</small> : null}
      </div>
      {renderError ? <div className="render-error" role="alert">{renderError}</div> : null}
    </div>
    {renderProgress ? <RenderProgressModal progress={renderProgress} onCancel={() => exportControllerRef.current?.abort()}/> : null}
    {isFullscreen ? createPortal(<div className="fullscreen-backdrop" aria-hidden="true"/>, document.body) : null}
  </>;
}

function MotionSelector({ selection, variant, exportFormat, cardDurationMs, canvasBackground, busy, onVariant, onExportFormat, onCardDurationMs, onCanvasBackground }: { selection: CreateShopVideoResponse["selection"]; variant: CarouselVariant; exportFormat: ExportFormat; cardDurationMs: number; canvasBackground: string; busy: boolean; onVariant: (variant: CarouselVariant) => void; onExportFormat: (format: ExportFormat) => void; onCardDurationMs: (durationMs: number) => void; onCanvasBackground: (background: string) => void }): ReactElement {
  const [sliderValue, setSliderValue] = useState(cardDurationMs);
  const [backgroundTab, setBackgroundTab] = useState<"solid" | "gradient">("solid");
  useEffect(() => setSliderValue(cardDurationMs), [cardDurationMs]);
  const speedLabel = sliderValue < 1_000 ? `${sliderValue} ms per card` : `${(sliderValue / 1_000).toFixed(sliderValue % 1_000 === 0 ? 0 : 2)} sec per card`;
  const commitSpeed = (value: number) => {
    setSliderValue(value);
    if (value !== cardDurationMs) onCardDurationMs(value);
  };
  return <div className="preset-section">
    <div className="motion-heading"><span><h3>Choose your motion</h3><small>Same creative. Different looks.</small></span></div>
    <div className="preset-grid">
      {VARIANTS.map((item) => <button type="button" key={item.id} className={variant === item.id ? "active" : ""} aria-pressed={variant === item.id} disabled={busy} onClick={() => onVariant(item.id)}>
        <i className={`motion-thumb is-${item.id}`}><img className="motion-preview" src={item.previewImage} alt="" /></i><span><strong>{item.label}</strong><small>{item.description}</small></span>
      </button>)}
    </div>
    <label className="carousel-speed-control">
      <span><strong>Carousel speed</strong><small>Choose how long each card stays in focus.</small></span>
      <input type="range" min={MIN_CARD_DURATION_MS} max={MAX_CARD_DURATION_MS} step="250" value={sliderValue} disabled={busy} onChange={(event) => setSliderValue(Number(event.target.value))} onPointerUp={(event) => commitSpeed(Number(event.currentTarget.value))} onKeyUp={(event) => commitSpeed(Number(event.currentTarget.value))} onBlur={(event) => commitSpeed(Number(event.currentTarget.value))}/>
      <output>{speedLabel}</output>
      <span className="carousel-speed-limits" aria-hidden="true"><small>250 ms</small><small>4 sec</small></span>
    </label>
    <section className="canvas-background" aria-labelledby="canvas-background-title">
      <span><strong id="canvas-background-title">Canvas background</strong><small>Applies to the full video canvas.</small></span>
      <div className="canvas-background-tabs" role="tablist" aria-label="Canvas background type">
        <button type="button" role="tab" aria-selected={backgroundTab === "solid"} className={backgroundTab === "solid" ? "active" : ""} onClick={() => setBackgroundTab("solid")}>Solid</button>
        <button type="button" role="tab" aria-selected={backgroundTab === "gradient"} className={backgroundTab === "gradient" ? "active" : ""} onClick={() => setBackgroundTab("gradient")}>Gradient</button>
      </div>
      {backgroundTab === "solid" ? <div className="background-options"><div className="background-swatches">{SOLID_BACKGROUNDS.map((background) => <button type="button" key={background} className={canvasBackground === background ? "active" : ""} style={{ background }} aria-label={`Use ${background} canvas background`} aria-pressed={canvasBackground === background} disabled={busy} onClick={() => onCanvasBackground(background)}/>)}</div><label className="custom-background"><span>Custom</span><input type="color" value={/^#[0-9a-f]{6}$/i.test(canvasBackground) ? canvasBackground : DEFAULT_CANVAS_BACKGROUND} disabled={busy} onChange={(event) => onCanvasBackground(event.target.value)}/></label></div> : <div className="background-swatches background-gradients">{GRADIENT_BACKGROUNDS.map((background, index) => <button type="button" key={background} className={canvasBackground === background ? "active" : ""} style={{ background }} aria-label={`Use gradient ${index + 1} canvas background`} aria-pressed={canvasBackground === background} disabled={busy} onClick={() => onCanvasBackground(background)}/>)}</div>}
    </section>
    <VideoFormatSelectionMobile value={exportFormat} disabled={busy} onChange={onExportFormat}/>
  </div>;
}

function VideoHeadlineEditor({ value, busy, onChange }: { value: VideoHeadline; busy: boolean; onChange: (value: VideoHeadline) => void }): ReactElement {
  return <section className="video-headline-editor" aria-labelledby="video-headline-title">
    <header><h3 id="video-headline-title">Video headline</h3><label><input type="checkbox" checked={value.showHeading} disabled={busy} onChange={(event) => onChange({ ...value, showHeading: event.target.checked })}/><span>Show headline</span></label></header>
    <label><input aria-label="Headline" type="text" value={value.headline} placeholder="Headline" maxLength={90} disabled={busy} onChange={(event) => onChange({ ...value, headline: event.target.value })}/></label>
    <label><input aria-label="Eyebrow text" type="text" value={value.eyebrow} placeholder="Eyebrow text" maxLength={48} disabled={busy} onChange={(event) => onChange({ ...value, eyebrow: event.target.value })}/></label>
    <div className="headline-color" role="group" aria-labelledby="headline-color-title">
      <span><strong id="headline-color-title">Headline color</strong><small>Applies to the main headline in your video.</small></span>
      <div className="headline-color-options">
        <div className="headline-color-swatches">
          {HEADLINE_COLORS.map((color) => <button type="button" key={color} className={value.color === color ? "active" : ""} style={{ background: color }} aria-label={`Use ${color} for the headline`} aria-pressed={value.color === color} disabled={busy} onClick={() => onChange({ ...value, color })}/>) }
        </div>
        <label className="custom-headline-color"><span>Custom</span><input aria-label="Custom headline color" type="color" value={/^#[0-9a-f]{6}$/i.test(value.color) ? value.color : "#fffaf6"} disabled={busy} onChange={(event) => onChange({ ...value, color: event.target.value })}/></label>
      </div>
    </div>
    <div className="eyebrow-color" role="group" aria-labelledby="eyebrow-color-title">
      <span><strong id="eyebrow-color-title">Eyebrow color</strong><small>Applies to the eyebrow above your headline.</small></span>
      <div className="eyebrow-color-options">
        <div className="eyebrow-color-swatches">
          {HEADLINE_COLORS.map((color) => <button type="button" key={color} className={value.eyebrowColor === color ? "active" : ""} style={{ background: color }} aria-label={`Use ${color} for the eyebrow`} aria-pressed={value.eyebrowColor === color} disabled={busy} onClick={() => onChange({ ...value, eyebrowColor: color })}/>) }
        </div>
        <label className="custom-eyebrow-color"><span>Custom</span><input aria-label="Custom eyebrow color" type="color" value={/^#[0-9a-f]{6}$/i.test(value.eyebrowColor) ? value.eyebrowColor : "#ff6577"} disabled={busy} onChange={(event) => onChange({ ...value, eyebrowColor: event.target.value })}/></label>
      </div>
    </div>
    <small>Displayed above your product carousel in the preview and final video.</small>
  </section>;
}

type ProductCardValues = Omit<ProductCardEdit, "productId">;
type SelectedCard = CreateShopVideoResponse["selection"]["selected"][number];

function cardValues(selected: SelectedCard): ProductCardValues {
  return {
    image: selected.product.primaryImage,
    price: selected.product.price,
    title: selected.product.title,
    layout: selected.cardLayout ?? "overlay",
    zoom: selected.imageTransform?.zoom ?? 1,
    offsetX: selected.imageTransform?.offsetX ?? 0,
    offsetY: selected.imageTransform?.offsetY ?? 0,
  };
}

function createBlankCard(productId: string): SelectedCard {
  return {
    product: {
      id: productId,
      title: "",
      price: "",
      listingUrl: "",
      primaryImage: "",
      alternateImages: [],
      imageWidth: 1200,
      imageHeight: 1600,
      favorites: 0,
      featured: false,
    },
    score: 0,
    reasons: [],
    cardLayout: "catalogue",
    imageTransform: { zoom: 1, offsetX: 0, offsetY: 0 },
  };
}

function maxImageOffset(zoom: number): number {
  return Math.min(100, Math.max(0, (zoom - 1) * 50));
}

function imageFileName(image: string): string {
  if (!image) return "No image selected";
  if (image.startsWith("blob:") || image.startsWith("data:")) return "Selected image";
  return image.split("/").pop()?.split("?")[0] || "Current image";
}

function ProductCardEditorModal({ selected, initial, onClose, onSave, onDelete }: { selected: SelectedCard; initial: ProductCardValues; onClose: () => void; onSave: (edit: ProductCardValues) => void; onDelete?: () => void }): ReactElement {
  const [edit, setEdit] = useState(initial);
  const [selectedFileName, setSelectedFileName] = useState(imageFileName(initial.image));
  const [mobileEditor, setMobileEditor] = useState(() => window.matchMedia("(max-width: 820px)").matches);
  const [mobileImageEditing, setMobileImageEditing] = useState(false);
  const [closing, setClosing] = useState(false);
  const editRef = useRef(edit);
  const dragRef = useRef<{ pointerId: number; startX: number; startY: number; offsetX: number; offsetY: number } | null>(null);
  const pointerPositionsRef = useRef(new Map<number, { x: number; y: number }>());
  const pinchRef = useRef<{ distance: number; zoom: number } | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const localImageUrlRef = useRef<string | null>(null);
  const closeActionRef = useRef<(() => void) | null>(null);
  const savedRef = useRef(false);
  const { product } = selected;
  editRef.current = edit;

  const closeWith = useCallback((action: () => void) => {
    if (closing) return;
    if (!mobileEditor || !window.matchMedia("(max-width: 820px)").matches || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      action();
      return;
    }
    closeActionRef.current = action;
    setClosing(true);
  }, [closing, mobileEditor]);

  const requestClose = useCallback(() => closeWith(onClose), [closeWith, onClose]);

  function finishAnimatedClose(): void {
    if (!closing) return;
    const action = closeActionRef.current;
    closeActionRef.current = null;
    action?.();
  }

  function saveCard(): void {
    closeWith(() => {
      savedRef.current = true;
      onSave(edit);
    });
  }

  function deleteCard(): void {
    if (onDelete) closeWith(onDelete);
  }

  function chooseImage(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    if (!file) return;
    if (localImageUrlRef.current) URL.revokeObjectURL(localImageUrlRef.current);
    const imageUrl = URL.createObjectURL(file);
    localImageUrlRef.current = imageUrl;
    setSelectedFileName(file.name);
    setMobileImageEditing(false);
    setEdit((current) => ({ ...current, image: imageUrl, zoom: 1, offsetX: 0, offsetY: 0 }));
  }

  function openImagePicker(): void {
    if (!imageInputRef.current) return;
    imageInputRef.current.value = "";
    imageInputRef.current.click();
  }

  function beginImageDrag(event: ReactPointerEvent<HTMLDivElement>): void {
    if (mobileEditor && !mobileImageEditing) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    pointerPositionsRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (mobileEditor && pointerPositionsRef.current.size >= 2) {
      const [first, second] = Array.from(pointerPositionsRef.current.values());
      pinchRef.current = { distance: Math.hypot(second!.x - first!.x, second!.y - first!.y), zoom: edit.zoom };
      dragRef.current = null;
      return;
    }
    dragRef.current = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, offsetX: edit.offsetX, offsetY: edit.offsetY };
  }

  function moveImage(event: ReactPointerEvent<HTMLDivElement>): void {
    if (pointerPositionsRef.current.has(event.pointerId)) pointerPositionsRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (mobileEditor && pinchRef.current && pointerPositionsRef.current.size >= 2) {
      event.preventDefault();
      const [first, second] = Array.from(pointerPositionsRef.current.values());
      const distance = Math.hypot(second!.x - first!.x, second!.y - first!.y);
      const zoom = Math.max(1, Math.min(3, pinchRef.current.zoom * distance / Math.max(1, pinchRef.current.distance)));
      const limit = maxImageOffset(zoom);
      setEdit((current) => ({ ...current, zoom, offsetX: Math.max(-limit, Math.min(limit, current.offsetX)), offsetY: Math.max(-limit, Math.min(limit, current.offsetY)) }));
      return;
    }
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const limit = maxImageOffset(edit.zoom);
    const offsetX = Math.max(-limit, Math.min(limit, drag.offsetX + (event.clientX - drag.startX) / bounds.width * 100));
    const offsetY = Math.max(-limit, Math.min(limit, drag.offsetY + (event.clientY - drag.startY) / bounds.height * 100));
    setEdit((current) => ({ ...current, offsetX, offsetY }));
  }

  function endImageDrag(event: ReactPointerEvent<HTMLDivElement>): void {
    pointerPositionsRef.current.delete(event.pointerId);
    if (pointerPositionsRef.current.size < 2) pinchRef.current = null;
    if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
  }

  useEffect(() => {
    const media = window.matchMedia("(max-width: 820px)");
    const updateMobileEditor = () => setMobileEditor(media.matches);
    media.addEventListener("change", updateMobileEditor);
    return () => media.removeEventListener("change", updateMobileEditor);
  }, []);

  useEffect(() => {
    if (mobileEditor) return;
    const media = document.querySelector<HTMLDivElement>(".card-editor-preview-card .hfp-card-media");
    if (!media) return;
    const resizeWithWheel = (event: WheelEvent) => {
      const current = editRef.current;
      const pixels = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaMode === 2 ? event.deltaY * window.innerHeight : event.deltaY;
      const zoom = Math.max(1, Math.min(3, current.zoom - Math.max(-120, Math.min(120, pixels)) * .0015));
      if (Math.abs(zoom - current.zoom) < .0001) return;
      event.preventDefault();
      const limit = maxImageOffset(zoom);
      const next = { ...current, zoom, offsetX: Math.max(-limit, Math.min(limit, current.offsetX)), offsetY: Math.max(-limit, Math.min(limit, current.offsetY)) };
      editRef.current = next;
      setEdit(next);
    };
    media.addEventListener("wheel", resizeWithWheel, { passive: false });
    return () => media.removeEventListener("wheel", resizeWithWheel);
  }, [mobileEditor]);

  useEffect(() => {
    const appRoot = document.getElementById("app-root");
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") requestClose();
    };
    appRoot?.setAttribute("inert", "");
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      appRoot?.removeAttribute("inert");
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [requestClose]);

  useEffect(() => () => {
    if (!savedRef.current && localImageUrlRef.current) URL.revokeObjectURL(localImageUrlRef.current);
  }, []);

  return createPortal(<div className={`card-editor-backdrop card-editor-sheet-backdrop${closing ? " is-closing" : ""}`}>
    <section className={`card-editor-modal card-editor-sheet${mobileEditor ? " is-mobile-editor" : ""}`} role="dialog" aria-modal="true" aria-labelledby="card-editor-title" onAnimationEnd={(event) => { if (event.target === event.currentTarget) finishAnimatedClose(); }}>
      <button className="card-editor-close" type="button" aria-label="Close card editor" onClick={requestClose}>×</button>
      <div className="card-editor-preview-pane">
        <span>Live card preview</span>
      <ProductCardView className="card-editor-preview-card" image={previewImageSource(edit.image)} title={edit.title.trim()} price={edit.price.trim()} layout={edit.layout} imageScale={edit.zoom} imageX={edit.offsetX} imageY={edit.offsetY} placeholderIcon="/assets/image-placeholder-icon.svg" mediaProps={{ className: `card-editor-draggable${mobileEditor && !mobileImageEditing ? " is-locked" : ""}`, onPointerDown: beginImageDrag, onPointerMove: moveImage, onPointerUp: endImageDrag, onPointerCancel: endImageDrag }} mediaOverlay={<><small className="card-drag-hint">Drag to reposition · Scroll to resize</small>{mobileEditor ? <span className="card-image-actions"><button className={`card-image-action-button card-image-edit-button${mobileImageEditing ? " is-save" : ""}`} type="button" onPointerDown={(event) => event.stopPropagation()} onClick={() => { if (mobileImageEditing) saveCard(); else setMobileImageEditing(true); }}>{mobileImageEditing ? "Save" : "Crop"}</button><button className="card-image-action-button card-image-change-button" type="button" onPointerDown={(event) => event.stopPropagation()} onClick={openImagePicker}>Image <i aria-hidden="true">↻</i></button></span> : null}</>}/>
      </div>
      <form className="card-editor-form" onSubmit={(event) => { event.preventDefault(); saveCard(); }}>
        <span className="card-editor-eyebrow">Customize your card</span>
        <h2 id="card-editor-title">Fine-tune the details</h2>
        <p>Choose your layout, adjust the image, and update the product details. See every change in the preview.</p>
        <label className="card-file-control"><span>Image</span><small>JPG, PNG, WebP or another image file</small><input ref={imageInputRef} required={!edit.image} type="file" accept="image/*" onChange={chooseImage}/><span className="card-file-picker"><i aria-hidden="true">+</i><strong>Choose image</strong><em>{selectedFileName}</em></span></label>
        <fieldset className="card-layout-control">
          <legend>Card layout</legend>
          <div>
            <button type="button" className={edit.layout === "overlay" ? "active" : ""} aria-pressed={edit.layout === "overlay"} onClick={() => setEdit((current) => ({ ...current, layout: "overlay" }))}><span className="layout-card-preview-wrap" aria-hidden="true"><ProductCardView as="div" className="layout-card-preview" image={previewImageSource(edit.image)} title={edit.title} price={edit.price} layout="overlay" imageScale={edit.zoom} imageX={edit.offsetX} imageY={edit.offsetY}/></span><span><strong>Overlay</strong><small>Text on image</small></span></button>
            <button type="button" className={edit.layout === "catalogue" ? "active" : ""} aria-pressed={edit.layout === "catalogue"} onClick={() => setEdit((current) => ({ ...current, layout: "catalogue" }))}><span className="layout-card-preview-wrap" aria-hidden="true"><ProductCardView as="div" className="layout-card-preview" image={previewImageSource(edit.image)} title={edit.title} price={edit.price} layout="catalogue" imageScale={edit.zoom} imageX={edit.offsetX} imageY={edit.offsetY}/></span><span><strong>Card</strong><small>Text below image</small></span></button>
          </div>
        </fieldset>
        <label className="card-zoom-control"><span>Image zoom</span><output>{edit.zoom.toFixed(2)}×</output><input aria-label="Image zoom" type="range" min="1" max="3" step="0.05" value={edit.zoom} onChange={(event) => { const zoom = Number(event.target.value); const limit = maxImageOffset(zoom); setEdit((current) => ({ ...current, zoom, offsetX: Math.max(-limit, Math.min(limit, current.offsetX)), offsetY: Math.max(-limit, Math.min(limit, current.offsetY)) })); }}/></label>
        <label><span>Product text</span><input type="text" value={edit.title} maxLength={90} onChange={(event) => setEdit((current) => ({ ...current, title: event.target.value }))}/></label>
        <label><span>Subtext (e.g., price)</span><input type="text" value={edit.price} maxLength={24} onChange={(event) => setEdit((current) => ({ ...current, price: event.target.value }))}/></label>
        <div className={`card-editor-actions${onDelete ? " has-delete" : ""}`}>{onDelete ? <button className="card-editor-delete" type="button" onClick={deleteCard}>Delete card</button> : null}<button className="card-editor-cancel" type="button" onClick={requestClose}>Cancel</button><button className="card-editor-save" type="submit">Save card</button></div>
      </form>
    </section>
  </div>, document.body);
}

function DeleteCardModal({ selected, onClose, onConfirm }: { selected: SelectedCard; onClose: () => void; onConfirm: () => void }): ReactElement {
  const values = cardValues(selected);

  useEffect(() => {
    const appRoot = document.getElementById("app-root");
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    appRoot?.setAttribute("inert", "");
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      appRoot?.removeAttribute("inert");
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  return createPortal(<div className="card-editor-backdrop">
    <section className="card-editor-modal card-delete-modal" role="dialog" aria-modal="true" aria-labelledby="card-delete-title">
      <button className="card-editor-close card-delete-close" type="button" aria-label="Close delete confirmation" onClick={onClose}>×</button>
      <div className="card-editor-preview-pane">
        <span>Card to remove</span>
        <ProductCardView className="card-editor-preview-card" image={previewImageSource(values.image)} title={values.title} price={values.price} layout={values.layout} imageScale={values.zoom} imageX={values.offsetX} imageY={values.offsetY}/>
      </div>
      <div className="card-delete-copy">
        <span className="card-editor-eyebrow">Edit the collection</span>
        <h2 id="card-delete-title">Remove this product card?</h2>
        <p>The card will disappear from the collection, carousel preview, and your next MP4 render.</p>
        <div className="card-editor-actions card-delete-actions"><button type="button" onClick={onClose}>Keep card</button><button type="button" autoFocus onClick={onConfirm}>Delete card</button></div>
      </div>
    </section>
  </div>, document.body);
}

function ProductGrid({ selection, edits, busy, onEdit, onAdd, onDelete }: { selection: CreateShopVideoResponse["selection"]; edits: Record<string, ProductCardEdit>; busy: boolean; onEdit: (edit: ProductCardEdit) => void; onAdd: (edit: ProductCardEdit) => void; onDelete: (productId: string) => void }): ReactElement {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [newCardId, setNewCardId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const active = selection.selected.find(({ product }) => product.id === activeId) ?? null;
  const deleting = selection.selected.find(({ product }) => product.id === deleteId) ?? null;
  const newCard = newCardId ? createBlankCard(newCardId) : null;
  const canAdd = selection.selected.length < 8;
  const canDelete = selection.selected.length > 3;

  return <>
    <div className="product-grid">
      <button className="add-card-button" type="button" disabled={!canAdd || busy} onClick={() => setNewCardId(`custom-card-${Date.now().toString(36)}`)}><span aria-hidden="true">+</span><strong>{canAdd ? "Add card" : "8 card maximum"}</strong></button>
      <div className="product-grid-body" aria-label="Product card templates">{selection.selected.map((selected) => {
            const { product } = selected;
            const edit = edits[product.id] ?? cardValues(selected);
            return <div className="editable-card-shell" key={product.id} role="button" tabIndex={0} aria-label={`Edit ${edit.title || "product card"}`} onClick={() => setActiveId(product.id)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setActiveId(product.id); } }}>
              <ProductCardView className="is-editable" image={previewImageSource(edit.image)} title={edit.title} price={edit.price} layout={edit.layout} imageScale={edit.zoom} imageX={edit.offsetX} imageY={edit.offsetY}/>
            </div>;
          })}</div>
    </div>
    {active ? <ProductCardEditorModal key={active.product.id} selected={active} initial={edits[active.product.id] ?? cardValues(active)} onClose={() => setActiveId(null)} onSave={(edit) => { onEdit({ productId: active.product.id, ...edit }); setActiveId(null); }} onDelete={canDelete ? () => { setActiveId(null); setDeleteId(active.product.id); } : undefined}/> : null}
    {newCard ? <ProductCardEditorModal key={newCard.product.id} selected={newCard} initial={{ ...cardValues(newCard), isNew: true }} onClose={() => setNewCardId(null)} onSave={(edit) => { onAdd({ productId: newCard.product.id, ...edit, isNew: true }); setNewCardId(null); }}/> : null}
    {deleting ? <DeleteCardModal key={deleting.product.id} selected={deleting} onClose={() => setDeleteId(null)} onConfirm={() => { onDelete(deleting.product.id); setDeleteId(null); }}/>: null}
  </>;
}

function CreativePresentation({ page, draft, variant, exportFormat, cardDurationMs, canvasBackground, videoHeadline, productEdits, busy, onVariant, onExportFormat, onCardDurationMs, onCanvasBackground, onVideoHeadlineChange, onProductEdit, onProductAdd, onProductDelete }: { page: PageVariant; draft: CreateShopVideoResponse; variant: CarouselVariant; exportFormat: ExportFormat; cardDurationMs: number; canvasBackground: string; videoHeadline: VideoHeadline; productEdits: Record<string, ProductCardEdit>; busy: boolean; onVariant: (variant: CarouselVariant) => void; onExportFormat: (format: ExportFormat) => void; onCardDurationMs: (durationMs: number) => void; onCanvasBackground: (background: string) => void; onVideoHeadlineChange: (value: VideoHeadline) => void; onProductEdit: (edit: ProductCardEdit) => void; onProductAdd: (edit: ProductCardEdit) => void; onProductDelete: (productId: string) => void }): ReactElement {
  const { shop, selection, project, previewHtml, exportHtml, duration } = draft;
  const presentation = getExportFormat(exportFormat).presentation;
  return <div className={`creative-presentation is-${presentation}`} data-presentation={presentation}>
    <div className="creative-preview-column">
      <VideoPreview html={previewHtml} exportHtml={exportHtml} duration={duration} width={project.format.width} height={project.format.height} fps={project.format.fps} filename={exportFilename(exportFormat)} title={page.previewTitle}/>
    </div>
    <section className="mobile-export-notice" aria-label="Desktop export notice">
      <header><span aria-hidden="true">▣</span><div><h2>Edit and preview on mobile. Download on desktop.</h2><p>Create your cards and preview your video here. Open this page on a computer to export your MP4.</p></div></header>
    </section>
    <div className="creative-controls-column">
      <VideoFormatSelectionBox value={exportFormat} disabled={busy} onChange={onExportFormat}/>
      <div className="creative-products-column">
        <header><h2>Your cards</h2><span>{selection.selected.length} of 8 cards</span></header>
        <ProductGrid selection={selection} edits={productEdits} busy={busy} onEdit={onProductEdit} onAdd={onProductAdd} onDelete={onProductDelete}/>
      </div>
      <VideoHeadlineEditor value={videoHeadline} busy={busy} onChange={onVideoHeadlineChange}/>
      <MotionSelector selection={selection} variant={variant} exportFormat={exportFormat} cardDurationMs={cardDurationMs} canvasBackground={canvasBackground} busy={busy} onVariant={onVariant} onExportFormat={onExportFormat} onCardDurationMs={onCardDurationMs} onCanvasBackground={onCanvasBackground}/>
    </div>
  </div>;
}

function Result({ page, draft, variant, exportFormat, cardDurationMs, canvasBackground, videoHeadline, productEdits, busy, onVariant, onExportFormat, onCardDurationMs, onCanvasBackground, onVideoHeadlineChange, onProductEdit, onProductAdd, onProductDelete }: { page: PageVariant; draft: CreateShopVideoResponse; variant: CarouselVariant; exportFormat: ExportFormat; cardDurationMs: number; canvasBackground: string; videoHeadline: VideoHeadline; productEdits: Record<string, ProductCardEdit>; busy: boolean; onVariant: (variant: CarouselVariant) => void; onExportFormat: (format: ExportFormat) => void; onCardDurationMs: (durationMs: number) => void; onCanvasBackground: (background: string) => void; onVideoHeadlineChange: (value: VideoHeadline) => void; onProductEdit: (edit: ProductCardEdit) => void; onProductAdd: (edit: ProductCardEdit) => void; onProductDelete: (productId: string) => void }): ReactElement {
  return <section className="result" aria-live="polite">
    <CreativePresentation page={page} draft={draft} variant={variant} exportFormat={exportFormat} cardDurationMs={cardDurationMs} canvasBackground={canvasBackground} videoHeadline={videoHeadline} productEdits={productEdits} busy={busy} onVariant={onVariant} onExportFormat={onExportFormat} onCardDurationMs={onCardDurationMs} onCanvasBackground={onCanvasBackground} onVideoHeadlineChange={onVideoHeadlineChange} onProductEdit={onProductEdit} onProductAdd={onProductAdd} onProductDelete={onProductDelete}/>
  </section>;
}

function App(): ReactElement {
  const page = resolvePageVariant(window.location.pathname);
  const [variant, setVariant] = useState<CarouselVariant>("curved-focus-carousel");
  const [exportFormat, setExportFormat] = useState<ExportFormat>(DEFAULT_EXPORT_FORMAT);
  const [cardDurationMs, setCardDurationMs] = useState(DEFAULT_CARD_DURATION_MS);
  const [canvasBackground, setCanvasBackground] = useState(DEFAULT_CANVAS_BACKGROUND);
  const [videoHeadline, setVideoHeadline] = useState<VideoHeadline | null>(null);
  const [productEdits, setProductEdits] = useState<Record<string, ProductCardEdit>>({});
  const [deletedProductIds, setDeletedProductIds] = useState<string[]>([]);
  const [draft, setDraft] = useState<CreateShopVideoResponse | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = page.documentTitle;
  }, [page.documentTitle]);

  useEffect(() => {
    void createDraft("curved-focus-carousel", DEFAULT_EXPORT_FORMAT);
  }, []);

  async function createDraft(nextVariant: CarouselVariant, nextFormat: ExportFormat, nextProductEdits: Record<string, ProductCardEdit> = productEdits, nextDeletedProductIds: string[] = deletedProductIds, nextCardDurationMs: number = cardDurationMs, nextCanvasBackground: string = canvasBackground, nextVideoHeadline: VideoHeadline | null = videoHeadline): Promise<void> {
    setLoading(true);
    setError("");
    try {
      const draft = await createShopVideoDraft(page.requestInput, dataSource, nextVariant, nextFormat, page.id, Object.values(nextProductEdits), nextDeletedProductIds, nextCardDurationMs, nextCanvasBackground, nextVideoHeadline ?? undefined);
      const payload: CreateShopVideoResponse = {
        ...draft,
        previewHtml: compileProjectPreviewDocument(createPreviewProject(draft.project)),
        exportHtml: compileProjectPreviewDocument(draft.project),
      };
      setVariant(nextVariant);
      setExportFormat(nextFormat);
      setCardDurationMs(nextCardDurationMs);
      setCanvasBackground(nextCanvasBackground);
      if (!nextVideoHeadline) {
        const content = draft.project.scenes[0]?.content;
        if (content && typeof content === "object" && !Array.isArray(content)) {
          const values = content as Record<string, unknown>;
          setVideoHeadline({ headline: typeof values.headline === "string" ? values.headline : "", eyebrow: typeof values.eyebrow === "string" ? values.eyebrow : "", showHeading: values.showHeading !== false, color: typeof draft.project.theme.palette.foreground === "string" ? draft.project.theme.palette.foreground : "#fffaf6", eyebrowColor: typeof values.eyebrowColor === "string" ? values.eyebrowColor : "#ff6577" });
        }
      }
      setDraft(payload);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : page.draftError);
    } finally {
      setLoading(false);
    }
  }

  return <>
    <style>{productCardCss()}</style>
    <HeaderNoLinks homeHref={page.path}/>
    <ProcessOverview/>
    <main>
      {page.id === "etsy" ? <section className="hero">
        {page.heroEyebrow ? <span className="eyebrow">{page.heroEyebrow}</span> : null}
        <h1>{page.heroLineOne}<br/><em>{page.heroLineTwo}</em></h1>
        <p>{page.heroSupporting}</p>
        {error ? <div className="error" role="alert">{error}</div> : null}
      </section> : error ? <div className="error" role="alert">{error}</div> : null}
      {draft && videoHeadline ? <Result page={page} draft={draft} variant={variant} exportFormat={exportFormat} cardDurationMs={cardDurationMs} canvasBackground={canvasBackground} videoHeadline={videoHeadline} productEdits={productEdits} busy={loading} onVariant={(next) => void createDraft(next, exportFormat)} onExportFormat={(next) => void createDraft(variant, next)} onCardDurationMs={(next) => void createDraft(variant, exportFormat, productEdits, deletedProductIds, next)} onCanvasBackground={(next) => { setCanvasBackground(next); void createDraft(variant, exportFormat, productEdits, deletedProductIds, cardDurationMs, next); }} onVideoHeadlineChange={(next) => { setVideoHeadline(next); void createDraft(variant, exportFormat, productEdits, deletedProductIds, cardDurationMs, canvasBackground, next); }} onProductEdit={(edit) => { const nextEdits = { ...productEdits, [edit.productId]: edit }; setProductEdits(nextEdits); void createDraft(variant, exportFormat, nextEdits); }} onProductAdd={(edit) => { const nextEdits = { ...productEdits, [edit.productId]: edit }; setProductEdits(nextEdits); void createDraft(variant, exportFormat, nextEdits); }} onProductDelete={(productId) => { const nextDeleted = [...deletedProductIds, productId]; const nextEdits = { ...productEdits }; delete nextEdits[productId]; setProductEdits(nextEdits); setDeletedProductIds(nextDeleted); void createDraft(variant, exportFormat, nextEdits, nextDeleted); }}/>: <section className="empty-state"><span>01</span><p>{loading ? page.loadingCta : page.emptyState}</p></section>}
    </main>
    <footer><span>MotionTwist</span><p>{page.footer}</p></footer>
  </>;
}

createRoot(document.getElementById("app-root")!).render(<App/>);
